import { Children, cloneElement, FC, isValidElement, PropsWithChildren, ReactElement, ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import Portal from 'src/components/Portal';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { fadeInTop } from 'src/styles/animation';
import { hexToRgb } from 'src/utils/common';
type RenderButtonProp = (onClickButton: () => void) => React.ReactNode;
interface MenuProps {
  renderButton: RenderButtonProp;
  /** 메뉴 정렬 방향. 'start': 버튼 시작 끝 정렬, 'end': 버튼 끝 끝 정렬 (기본: 'start') */
  align?: 'start' | 'end';
  onOpenChange?: (open: boolean) => void;
}
const Root = styled.div`
  position: relative;
`;
const MenuContent = styled.div<{
  $align: 'start' | 'end';
}>`
  position: absolute;
  top: calc(100% + 10px);
  z-index: 99;
  ${({
  $align
}) => $align === 'end' ? 'inset-inline-end: 0;' : 'inset-inline-start: 0;'}

  ${({
  theme
}) => theme.screenSize.mobile} {
    position: fixed;
    inset-inline: 0;
    bottom: 0;
    top: auto;
    animation: ${fadeInTop} 0.4s ease-in-out;
  }
`;
const Backdrop = styled.div<{
  $isDim: boolean;
}>`
  position: fixed;
  z-index: 98;
  inset: 0;
  background: ${({
  theme,
  $isDim
}) => $isDim ? `rgba(${hexToRgb(theme.color.surfaceDeepFixed)}, .7)` : 'transparent'};
`;
const Menu: FC<PropsWithChildren<MenuProps>> = ({
  renderButton,
  children,
  align = 'start',
  onOpenChange
}) => {
  const device = useCssMediaDevice();
  const isMobile = device === 'mobile';
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
    onOpenChange?.(false);
  };
  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
  };
  const childWithClose = Children.map(children, (child: ReactElement<{
    onClose: () => void;
  }> | ReactNode | undefined) => isValidElement<{
    onClose: () => void;
  }>(child) && cloneElement(child, {
    onClose: handleClose
  }));
  const menuOverlay = open && <>
      <Backdrop $isDim={isMobile} onClick={e => {
      e.stopPropagation();
      handleClose();
    }} role='button' tabIndex={0} />
      <MenuContent $align={align} role='button' tabIndex={0} onClick={e => e.stopPropagation()}>
        {childWithClose}
      </MenuContent>
    </>;
  return <Root data-sentry-element="Root" data-sentry-component="Menu" data-sentry-source-file="index.tsx">
      {renderButton(toggleOpen)}
      {isMobile ? <Portal>{menuOverlay}</Portal> : menuOverlay}
    </Root>;
};
export default Menu;